import React, { useEffect, useState } from 'react';
import styled, { css } from 'astroturf/react';

import { Icon } from 'js/design_system/Icon';
import { Popover } from 'js/design_system/Popover';
import SearchBar from 'js/header/SearchBar/SearchBar';
import trackClick from 'js/utils/trackClick';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

import { barHeightsFor, resolveNavImpactStats } from '../arms';
import type { ImpactStats, NavImpactLinks, PremiumUpsells } from '../../types';
import AppsMegaMenu from './AppsMegaMenu';
import AvatarCell from './AvatarCell';
import BareSearchField from './BareSearchField';
import IconCell from './IconCell';
import LogoCell from './LogoCell';
import PremiumCtaCell from './PremiumCtaCell';
import ProfileDropdown from './ProfileDropdown';
import SearchCell from './SearchCell';
import StatCells from './StatCells';

const DesktopBar = styled.div`
  display: flex;
  align-items: stretch;
  box-sizing: border-box;
  width: 100%;
  height: 64px;
  background: var(--background-white, #ffffff);
  border-bottom: 1px solid var(--stroke-light, #dddde2);
`;

const MobileBar = styled.div`
  display: flex;
  align-items: stretch;
  box-sizing: border-box;
  width: 100%;
  height: 64px;
  padding-left: 16px;
  background: var(--background-white, #ffffff);
  border-top: 1px solid var(--stroke-light, #dddde2);
  border-bottom: 1px solid var(--stroke-light, #dddde2);
`;

const MobileLeftGroup = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  align-items: center;
  column-gap: 12px;
`;

const MobileIconButton = styled.button<{ tone?: 'grey' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--text-primary, #141413);
  cursor: pointer;

  &.tone-grey {
    color: var(--icon-grey, #b1b1ba);
  }
`;

const SearchFieldWatcher = styled(ImpressionWatcher)`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  align-items: center;
`;

const RightCluster = styled.div<{
  tint: 'white' | 'beige';
  withLeftBorder?: boolean;
}>`
  display: flex;
  flex: 0 0 auto;
  height: 100%;

  &.tint-beige {
    background: var(--background-beige-light, #f9f8f6);
  }

  &.withLeftBorder.tint-white {
    border-left: 1px solid var(--stroke-light, #dddde2);
  }

  &.withLeftBorder.tint-beige {
    border-left: 1px solid var(--stroke-beige-light, #ccc9bc);
  }
`;

const fullHeightActivator = css`
  display: flex;
  height: 100%;
`;

const fullScreenProfileContainer = css`
  @import 'stylesheets/design_system/breakpoint';

  @media (max-width: ($ds-tablet - 1px)) {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    max-width: none !important;
    height: 100% !important;
    max-height: none !important;
    pointer-events: none;
  }
`;

type OpenMenu = 'apps' | 'profile' | null;

export type ImpactNavBarUser = {
  name: string;
  affiliation?: string | null;
  avatarUrl: string;
  profileUrl: string;
};

export type ImpactNavBarProps = {
  arm: string | null | undefined;
  user: ImpactNavBarUser;
  impactStats?: ImpactStats | null;
  badgeCount: number;
  messagesCount: number;
  notificationCount: number | null;
  isPremium?: boolean;
  premiumUpsells?: PremiumUpsells;
  hidePremiumUpsellButton?: boolean;
  showPodcastLibraryNav?: boolean;
  hasUnseenClaimablePapers?: boolean;
  hasUnseenUploadableStubs?: boolean;
  initialQuery?: string;
  isMobileNav?: boolean;
  onMenuToggle?: (open: boolean) => void;
  testRunning?: boolean;
  hasUnseenMentions?: boolean;
  hasUnseenReaders?: boolean;
  links: NavImpactLinks;
};

const ImpactNavBar = ({
  arm,
  user,
  impactStats,
  badgeCount,
  messagesCount,
  notificationCount,
  isPremium = false,
  premiumUpsells,
  hidePremiumUpsellButton = false,
  showPodcastLibraryNav = false,
  hasUnseenClaimablePapers = false,
  hasUnseenUploadableStubs = false,
  initialQuery,
  isMobileNav = false,
  onMenuToggle,
  testRunning = false,
  hasUnseenMentions,
  hasUnseenReaders,
  links,
}: ImpactNavBarProps) => {
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    onMenuToggle?.(openMenu !== null);
  }, [openMenu, onMenuToggle]);

  const surfaces = resolveNavImpactStats(arm);
  const heights = barHeightsFor(surfaces);
  if (heights == null) {
    return null;
  }

  const bucket = arm ?? 'control';
  const tint = 'beige';

  const toggleMenu = (menu: 'apps' | 'profile') => {
    setOpenMenu(openMenu === menu ? null : menu);
  };

  // A popover may only close ITSELF: its dismissal must not clobber the other
  // menu's open state through the shared openMenu value.
  const menuChangeHandler = (menu: 'apps' | 'profile') => (open: boolean) => {
    setOpenMenu((prev) => {
      if (open) return menu;
      return prev === menu ? null : prev;
    });
  };

  const appsMenu = (trigger: React.ReactNode) => (
    <AppsMegaMenu
      open={openMenu === 'apps'}
      isPremium={isPremium}
      links={links.megaMenu}
      className={fullHeightActivator}
      onChange={menuChangeHandler('apps')}
    >
      {trigger}
    </AppsMegaMenu>
  );

  const avatarCell = (
    <Popover
      open={openMenu === 'profile'}
      placement="bottom-end"
      className={fullHeightActivator}
      containerClassName={fullScreenProfileContainer}
      render={() => (
        <ProfileDropdown
          userName={user.name}
          userAffiliation={user.affiliation}
          avatarUrl={user.avatarUrl}
          profileUrl={user.profileUrl}
          impactStats={impactStats}
          messagesCount={messagesCount}
          notificationCount={notificationCount}
          isPremium={isPremium}
          premiumUpsells={premiumUpsells}
          hasUnseenMentions={hasUnseenMentions}
          hasUnseenReaders={hasUnseenReaders}
          links={links}
          onClose={() => menuChangeHandler('profile')(false)}
        />
      )}
      onChange={menuChangeHandler('profile')}
    >
      <AvatarCell
        avatarUrl={user.avatarUrl}
        count={badgeCount}
        tint={tint}
        pressed={openMenu === 'profile'}
        onClick={() => {
          if (openMenu !== 'profile') {
            trackClick({ source: 'nav-bar--account-menu-button', bucket });
          }
          toggleMenu('profile');
        }}
      />
    </Popover>
  );

  if (isMobileNav) {
    return (
      <MobileBar data-testid="impact-nav-bar" data-bar-height={heights.mobile}>
        {searchOpen ? (
          <MobileLeftGroup data-testid="mobile-search-row">
            <MobileIconButton
              type="button"
              tone="grey"
              aria-label="Close search"
              onClick={() => setSearchOpen(false)}
              data-testid="mobile-search-close"
            >
              <Icon name="close" size={32} aria-hidden />
            </MobileIconButton>
            <SearchFieldWatcher source="focused_nav_search_bar">
              <BareSearchField>
                <SearchBar
                  initialQuery={initialQuery}
                  isPremium={isPremium}
                  placeholder="Search..."
                  hideScopeSelector
                />
              </BareSearchField>
            </SearchFieldWatcher>
          </MobileLeftGroup>
        ) : (
          <>
            <MobileLeftGroup>
              {appsMenu(
                <MobileIconButton
                  type="button"
                  aria-label="Apps menu"
                  aria-pressed={openMenu === 'apps'}
                  onClick={() => toggleMenu('apps')}
                  data-testid="mobile-apps-button"
                >
                  <Icon name="apps" size={32} aria-hidden />
                </MobileIconButton>,
              )}
              <MobileIconButton
                type="button"
                aria-label="Search"
                onClick={() => setSearchOpen(true)}
                data-testid="mobile-search-button"
              >
                <Icon name="search" size={32} aria-hidden />
              </MobileIconButton>
            </MobileLeftGroup>
            <RightCluster
              tint={tint}
              withLeftBorder
              data-testid="nav-right-cluster"
              data-tint={tint}
            >
              {avatarCell}
              <StatCells
                arm={arm}
                surfaces={surfaces}
                impactStats={impactStats}
                tint={tint}
                citationsOnly
                testRunning={testRunning}
                hasUnseenMentions={hasUnseenMentions}
                hasUnseenReaders={hasUnseenReaders}
                citationsHref={links.citations}
                paperViewsHref={links.paperViews}
              />
            </RightCluster>
          </>
        )}
      </MobileBar>
    );
  }

  return (
    <DesktopBar data-testid="impact-nav-bar" data-bar-height={heights.desktop}>
      <LogoCell href={links.home} />
      {appsMenu(
        <IconCell
          icon="apps"
          label="Apps menu"
          pressed={openMenu === 'apps'}
          onClick={() => toggleMenu('apps')}
        />,
      )}
      <IconCell
        icon="arrow_upward"
        label="Upload"
        href={
          hasUnseenClaimablePapers
            ? links.upload.claimPapers
            : hasUnseenUploadableStubs
              ? links.upload.claimStubs
              : links.upload.default
        }
        onClick={() => trackClick({ source: 'nav-bar--upload-button' })}
      />
      <SearchCell
        initialQuery={initialQuery}
        isPremium={isPremium}
        dividerTint={tint}
      />
      <RightCluster
        tint={tint}
        data-testid="nav-right-cluster"
        data-tint={tint}
      >
        {avatarCell}
        <StatCells
          arm={arm}
          surfaces={surfaces}
          impactStats={impactStats}
          tint={tint}
          testRunning={testRunning}
          hasUnseenMentions={hasUnseenMentions}
          hasUnseenReaders={hasUnseenReaders}
          citationsHref={links.citations}
          paperViewsHref={links.paperViews}
        />
      </RightCluster>
      <PremiumCtaCell
        isPremium={isPremium}
        hidePremiumUpsellButton={hidePremiumUpsellButton}
        showPodcastLibraryNav={showPodcastLibraryNav}
        premiumUpsells={premiumUpsells}
        premiumBases={links.premium}
        bucket={bucket}
      />
    </DesktopBar>
  );
};

export default ImpactNavBar;
